import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { useIsMobile } from '../hooks/useIsMobile';

const BG     = '#f2f2ee';
const BORDER = '#e5e0d5';
const MUTED  = '#8b7d6b';
const TEXT   = '#0A0A0A';
const FONT   = "'Courier Prime Sans', 'Courier New', monospace";
const SZ     = 'clamp(10px, 1vw, 16px)';

const CARD_TYPES = [
  { type: 'cover',   label: 'Cover' },
  { type: 'heading', label: 'Heading' },
  { type: 'text',    label: 'Text Block' },
  { type: 'image',   label: 'Image' },
  { type: 'sketch',  label: 'Sketch' },
  { type: 'color',   label: 'Color Palette' },
  { type: 'note',    label: 'Note' },
];

function defaultContent(type) {
  switch (type) {
    case 'cover':   return { title: 'Untitled Brief', subtitle: '', date: '' };
    case 'heading': return { text: 'Heading' };
    case 'text':    return { text: 'Write something here.' };
    case 'image':   return { src: '', caption: '' };
    case 'sketch':  return { src: '' };
    case 'color':   return { colors: ['#d4c5b0', '#8c7b6b', '#3d3530'], label: 'Palette' };
    case 'note':    return { text: '' };
    default:        return {};
  }
}

const CARD_W     = 'min(1125px, 94vw)';
const CARD_W_PX  = 1125;

// ── Card visuals ──────────────────────────────────────────────────────────────

function CoverCard({ content }) {
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: 'clamp(24px, 4vw, 48px) clamp(16px, 3vw, 32px)', textAlign: 'center' }}>
      {content.date && (
        <div style={{ fontSize: SZ, letterSpacing: '0.06em', color: MUTED, textTransform: 'uppercase', marginBottom: 28, fontFamily: FONT }}>
          {content.date}
        </div>
      )}
      <div style={{ fontSize: SZ, fontWeight: 400, letterSpacing: '0.06em', color: TEXT, margin: '0 0 20px', lineHeight: 1.6, fontFamily: FONT, textTransform: 'uppercase' }}>
        {content.title || 'Untitled'}
      </div>
      {content.subtitle && (
        <p style={{ fontSize: SZ, color: MUTED, fontFamily: FONT, margin: 0, fontWeight: 400, lineHeight: 1.6, letterSpacing: '0.06em' }}>
          {content.subtitle}
        </p>
      )}
      <div style={{ width: 32, height: 1, background: BORDER, margin: '40px auto 0' }} />
    </div>
  );
}

function HeadingCard({ content }) {
  return (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', padding: 'clamp(24px, 4vw, 48px) clamp(16px, 3vw, 32px)' }}>
      <div style={{ fontSize: SZ, fontWeight: 400, letterSpacing: '0.06em', color: TEXT, lineHeight: 1.6, fontFamily: FONT, textAlign: 'left', textTransform: 'uppercase' }}>
        {content.text}
      </div>
    </div>
  );
}

function TextCard({ content }) {
  return (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', padding: 'clamp(24px, 4vw, 48px) clamp(16px, 3vw, 32px)' }}>
      <p style={{ fontSize: SZ, color: TEXT, fontFamily: FONT, lineHeight: 1.65, margin: 0, whiteSpace: 'pre-wrap', textAlign: 'left', fontWeight: 400, letterSpacing: '0.02em' }}>
        {content.text}
      </p>
    </div>
  );
}

function ImageCard({ content, isAdmin, briefId, cardId, onRefresh }) {
  const [dragging,  setDragging]  = useState(false);
  const [uploading, setUploading] = useState(false);
  const [caption,   setCaption]   = useState(content.caption || '');
  const [editCap,   setEditCap]   = useState(false);

  // Keep caption state in sync if card refreshes from outside
  useState(() => { setCaption(content.caption || ''); }, [content.caption]);

  async function uploadFile(file) {
    if (!file || !file.type.startsWith('image/')) return;
    setUploading(true);
    try {
      await api.briefs.uploadImage(briefId, cardId, file, content.caption || '');
      onRefresh();
    } catch (e) { alert(e.message); }
    finally { setUploading(false); setDragging(false); }
  }

  async function saveCaption() {
    setEditCap(false);
    if (caption === content.caption) return;
    try {
      await api.briefs.updateCard(briefId, cardId, { ...content, caption });
      onRefresh();
    } catch (e) { console.error(e); }
  }

  const hasCaption = Boolean(content.caption) || editCap;

  return (
    <div
      style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: hasCaption ? '40px 32px 28px' : '40px 32px', position: 'relative' }}
      onDragOver={isAdmin ? e => { e.preventDefault(); setDragging(true); } : undefined}
      onDragLeave={isAdmin ? () => setDragging(false) : undefined}
      onDrop={isAdmin ? e => { e.preventDefault(); uploadFile(e.dataTransfer.files[0]); } : undefined}
    >
      {/* Drag overlay */}
      {dragging && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(242,242,238,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, border: `2px dashed ${BORDER}` }}>
          <span style={{ fontSize: SZ, letterSpacing: '0.06em', color: MUTED, textTransform: 'uppercase', fontFamily: FONT }}>Drop to upload</span>
        </div>
      )}

      {content.src ? (
        <>
          {/* Image — click to replace via file picker in admin mode */}
          <div style={{ flex: '0 1 auto', maxHeight: 'calc(100% - 56px)', width: '100%', display: 'flex', justifyContent: 'center', position: 'relative' }}>
            {uploading && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(242,242,238,0.7)' }}>
                <span style={{ fontSize: SZ, color: MUTED, fontFamily: FONT, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Uploading…</span>
              </div>
            )}
            <img
              src={`${api.BASE_URL}${content.src}`}
              alt={caption || ''}
              style={{ maxWidth: '100%', maxHeight: hasCaption ? 'calc(100vh - 200px)' : 'calc(100vh - 160px)', objectFit: 'contain', display: 'block' }}
            />
          </div>

          {/* Caption — inline editable for admin */}
          {isAdmin ? (
            editCap ? (
              <textarea
                autoFocus
                value={caption}
                onChange={e => setCaption(e.target.value)}
                onBlur={saveCaption}
                onKeyDown={e => { if (e.key === 'Escape') saveCaption(); }}
                style={{ fontSize: SZ, color: TEXT, fontFamily: FONT, margin: '16px 0 0', textAlign: 'center', lineHeight: 1.5, width: '100%', maxWidth: 700, letterSpacing: '0.04em', background: 'transparent', border: 'none', borderBottom: `1px solid ${BORDER}`, outline: 'none', resize: 'none', padding: '2px 0' }}
                rows={2}
              />
            ) : (
              <p
                onClick={() => setEditCap(true)}
                title="Click to edit caption"
                style={{ fontSize: SZ, color: caption ? TEXT : MUTED, fontFamily: FONT, margin: '16px 0 0', textAlign: 'center', lineHeight: 1.5, width: '100%', maxWidth: 700, letterSpacing: '0.04em', cursor: 'text', minHeight: 20 }}
              >
                {caption}
              </p>
            )
          ) : (
            caption && (
              <p style={{ fontSize: SZ, color: TEXT, fontFamily: FONT, margin: '16px 0 0', textAlign: 'center', lineHeight: 1.5, width: '100%', maxWidth: 700, letterSpacing: '0.04em' }}>
                {caption}
              </p>
            )
          )}
        </>
      ) : (
        /* Empty state — drop zone */
        <label style={{ width: '100%', height: '60%', border: `1px dashed ${BORDER}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, cursor: isAdmin ? 'pointer' : 'default' }}>
          {isAdmin && <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => uploadFile(e.target.files[0])} />}
          <span style={{ fontSize: SZ, letterSpacing: '0.06em', color: MUTED, textTransform: 'uppercase', fontFamily: FONT }}>
            {isAdmin ? 'Drop or click to upload' : 'No image uploaded'}
          </span>
        </label>
      )}
    </div>
  );
}

function SketchCard({ content, isAdmin, briefId, cardId, onRefresh }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing]       = useState(false);
  const [color, setColor]           = useState('#0A0A0A');
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [eraser, setEraser]         = useState(false);
  const [uploading, setUploading]   = useState(false);
  const lastPos = useRef(null);

  function initCanvas() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  useEffect(() => {
    if (content.src) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      };
      img.src = `${api.BASE_URL}${content.src}`;
    } else {
      initCanvas();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content.src]);

  function getPos(e) {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - rect.left) * sx, y: (src.clientY - rect.top) * sy };
  }

  function startDraw(e) {
    if (!isAdmin) return;
    e.preventDefault();
    setDrawing(true);
    const pos = getPos(e);
    lastPos.current = pos;
    const ctx = canvasRef.current.getContext('2d');
    ctx.globalCompositeOperation = eraser ? 'destination-out' : 'source-over';
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, strokeWidth / 2, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
  }

  function draw(e) {
    if (!drawing || !isAdmin) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.globalCompositeOperation = eraser ? 'destination-out' : 'source-over';
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = color;
    ctx.lineWidth = strokeWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPos.current = pos;
  }

  function stopDraw() { setDrawing(false); lastPos.current = null; }

  async function saveSketch() {
    setUploading(true);
    try {
      await new Promise((resolve, reject) => {
        canvasRef.current.toBlob(async blob => {
          try {
            await api.briefs.uploadImage(briefId, cardId, new File([blob], 'sketch.png', { type: 'image/png' }), '');
            resolve();
          } catch (err) { reject(err); }
        }, 'image/png');
      });
      onRefresh();
    } catch (err) { alert(err.message); }
    finally { setUploading(false); }
  }

  const PALETTE = ['#0A0A0A', '#8b7d6b', '#c0392b', '#2980b9', '#27ae60', '#f39c12'];
  const WIDTHS  = [1, 3, 6, 12];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: isAdmin ? '16px 32px' : '40px 32px', gap: 10 }}>
      {isAdmin && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'center' }}>
          {PALETTE.map(c => (
            <button key={c} onClick={() => { setColor(c); setEraser(false); }}
              style={{ width: 18, height: 18, background: c, border: (c === color && !eraser) ? `2px solid ${TEXT}` : `1px solid ${BORDER}`, cursor: 'pointer', padding: 0, borderRadius: '50%' }} />
          ))}
          <div style={{ width: 1, height: 18, background: BORDER }} />
          {WIDTHS.map(w => (
            <button key={w} onClick={() => setStrokeWidth(w)}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: SZ, color: strokeWidth === w ? TEXT : MUTED, fontFamily: FONT, letterSpacing: '0.06em' }}>
              {w}
            </button>
          ))}
          <div style={{ width: 1, height: 18, background: BORDER }} />
          <button onClick={() => setEraser(v => !v)}
            style={{ background: eraser ? TEXT : 'transparent', color: eraser ? '#fff' : MUTED, border: eraser ? 'none' : `1px solid ${BORDER}`, cursor: 'pointer', fontSize: SZ, fontFamily: FONT, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '3px 8px' }}>
            Eraser
          </button>
          <button onClick={initCanvas}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: SZ, color: MUTED, fontFamily: FONT, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '3px 8px' }}>
            Clear
          </button>
          <button onClick={saveSketch} disabled={uploading}
            style={{ background: TEXT, color: '#fff', border: 'none', cursor: uploading ? 'default' : 'pointer', fontSize: SZ, fontFamily: FONT, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '5px 14px' }}>
            {uploading ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
      <canvas
        ref={canvasRef}
        width={1080}
        height={720}
        style={{ border: `1px solid ${BORDER}`, maxWidth: '100%', maxHeight: isAdmin ? 'calc(100% - 68px)' : '100%', cursor: isAdmin ? 'crosshair' : 'default', touchAction: 'none', display: 'block' }}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={stopDraw}
        onMouseLeave={stopDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={stopDraw}
      />
    </div>
  );
}

function ColorCard({ content }) {
  const colors = content.colors || [];
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 'clamp(24px, 4vw, 48px) clamp(16px, 3vw, 32px)' }}>
      {content.label && (
        <div style={{ fontSize: SZ, letterSpacing: '0.06em', color: MUTED, textTransform: 'uppercase', marginBottom: 40, fontFamily: FONT }}>
          {content.label}
        </div>
      )}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', justifyContent: 'center' }}>
        {colors.map((color, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 56, height: 110, background: color, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }} />
            <div style={{ fontSize: SZ, letterSpacing: '0.06em', color: MUTED, fontFamily: 'monospace' }}>{color}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NoteCard({ content }) {
  return (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', padding: 'clamp(24px, 4vw, 48px) clamp(16px, 3vw, 32px)' }}>
      <div style={{ width: '100%', maxWidth: 560 }}>
        <div style={{ fontSize: SZ, letterSpacing: '0.08em', color: MUTED, textTransform: 'uppercase', marginBottom: 24, fontFamily: FONT }}>Note</div>
        <div style={{ fontSize: SZ, color: TEXT, fontFamily: FONT, lineHeight: 1.65, whiteSpace: 'pre-wrap', letterSpacing: '0.02em' }}>
          {content.text}
        </div>
      </div>
    </div>
  );
}

function CardContent({ card, isAdmin, briefId, onRefresh }) {
  switch (card.type) {
    case 'cover':   return <CoverCard content={card.content} />;
    case 'heading': return <HeadingCard content={card.content} />;
    case 'text':    return <TextCard content={card.content} />;
    case 'image':   return <ImageCard content={card.content} isAdmin={isAdmin} briefId={briefId} cardId={card.id} onRefresh={onRefresh} />;
    case 'sketch':  return <SketchCard content={card.content} isAdmin={isAdmin} briefId={briefId} cardId={card.id} onRefresh={onRefresh} />;
    case 'color':   return <ColorCard content={card.content} />;
    case 'note':    return <NoteCard content={card.content} />;
    default:        return null;
  }
}

// ── Card editor ───────────────────────────────────────────────────────────────

function CardEditor({ card, briefId, onUpdate, onClose }) {
  const [content, setContent] = useState({ ...card.content });
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const set = (k, v) => setContent(p => ({ ...p, [k]: v }));

  async function save() {
    setSaving(true);
    try {
      await api.briefs.updateCard(briefId, card.id, content);
      if (imageFile && card.type === 'image') {
        await api.briefs.uploadImage(briefId, card.id, imageFile, content.caption);
      }
      onUpdate(await api.briefs.get(briefId));
      onClose();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  }

  const iS = { width: '100%', background: '#fff', border: `1px solid ${BORDER}`, color: TEXT, fontSize: 13, padding: '10px 12px', outline: 'none', fontFamily: FONT, boxSizing: 'border-box', marginBottom: 14 };
  const lS = { fontSize: SZ, letterSpacing: '0.06em', color: MUTED, textTransform: 'uppercase', display: 'block', marginBottom: 7, fontFamily: FONT };

  return (
    <div style={{ padding: 24, overflowY: 'auto', height: '100%', boxSizing: 'border-box' }}>
      <div style={{ fontSize: SZ, letterSpacing: '0.06em', color: MUTED, textTransform: 'uppercase', marginBottom: 24, fontFamily: FONT }}>
        Edit — {card.type}
      </div>

      {card.type === 'cover' && (<>
        <label style={lS}>Title</label>
        <input style={iS} value={content.title || ''} onChange={e => set('title', e.target.value)} />
        <label style={lS}>Subtitle</label>
        <input style={iS} value={content.subtitle || ''} onChange={e => set('subtitle', e.target.value)} />
        <label style={lS}>Date</label>
        <input style={iS} value={content.date || ''} onChange={e => set('date', e.target.value)} placeholder="e.g. May 2026" />
      </>)}

      {card.type === 'heading' && (<>
        <label style={lS}>Text</label>
        <textarea style={{ ...iS, resize: 'vertical', minHeight: 100 }} value={content.text || ''} onChange={e => set('text', e.target.value)} />
      </>)}

      {card.type === 'text' && (<>
        <label style={lS}>Body</label>
        <textarea style={{ ...iS, resize: 'vertical', minHeight: 180 }} value={content.text || ''} onChange={e => set('text', e.target.value)} />
      </>)}

      {card.type === 'note' && (<>
        <label style={lS}>Note</label>
        <textarea style={{ ...iS, resize: 'vertical', minHeight: 140 }} value={content.text || ''} onChange={e => set('text', e.target.value)} />
      </>)}

      {card.type === 'image' && (<>
        <label style={lS}>Image File</label>
        <input type="file" accept="image/*" onChange={e => setImageFile(e.target.files[0])}
          style={{ ...iS, color: '#555', cursor: 'pointer', padding: '8px 12px' }} />
        <label style={lS}>Caption</label>
        <input style={iS} value={content.caption || ''} onChange={e => set('caption', e.target.value)} placeholder="e.g. Hard light with shadow cuts" />
      </>)}

      {card.type === 'color' && (<>
        <label style={lS}>Label</label>
        <input style={iS} value={content.label || ''} onChange={e => set('label', e.target.value)} />
        <label style={lS}>Hex colors — one per line</label>
        <textarea
          style={{ ...iS, resize: 'vertical', minHeight: 100, fontFamily: 'monospace', fontSize: 12 }}
          value={(content.colors || []).join('\n')}
          onChange={e => set('colors', e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}
          placeholder={'#d4c5b0\n#8c7b6b\n#3d3530'}
        />
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {(content.colors || []).map((c, i) => <div key={i} style={{ width: 28, height: 28, background: c, border: `1px solid ${BORDER}` }} title={c} />)}
        </div>
      </>)}

      <button onClick={save} disabled={saving} style={{
        width: '100%', background: saving ? MUTED : TEXT, color: '#fff', border: 'none',
        padding: '13px', fontSize: SZ, letterSpacing: '0.06em', textTransform: 'uppercase',
        cursor: saving ? 'default' : 'pointer', fontFamily: FONT, marginTop: 4,
      }}>{saving ? 'Saving...' : 'Save'}</button>
      <button onClick={onClose} style={{
        width: '100%', background: 'transparent', color: MUTED, border: 'none',
        padding: '10px', fontSize: SZ, letterSpacing: '0.06em', textTransform: 'uppercase',
        cursor: 'pointer', fontFamily: FONT, marginTop: 6,
      }}>Cancel</button>
    </div>
  );
}

// ── Add card panel ────────────────────────────────────────────────────────────

function AddCardPanel({ onAdd, onClose }) {
  return (
    <div style={{ padding: 24 }}>
      <div style={{ fontSize: SZ, letterSpacing: '0.06em', color: MUTED, textTransform: 'uppercase', marginBottom: 24, fontFamily: FONT }}>Add Card</div>
      {CARD_TYPES.map(({ type, label }) => (
        <button key={type} onClick={() => onAdd(type)} style={{
          display: 'block', width: '100%', textAlign: 'left', background: 'transparent',
          border: 'none', borderBottom: `1px solid ${BORDER}`, color: TEXT,
          padding: '14px 0', fontSize: SZ, letterSpacing: '0.06em', textTransform: 'uppercase',
          cursor: 'pointer', fontFamily: FONT,
        }}>
          {label}
        </button>
      ))}
      <button onClick={onClose} style={{
        width: '100%', background: 'transparent', border: 'none', color: MUTED,
        padding: '12px 0', fontSize: SZ, letterSpacing: '0.06em', textTransform: 'uppercase',
        cursor: 'pointer', fontFamily: FONT, marginTop: 10,
      }}>
        Cancel
      </button>
    </div>
  );
}

// ── Edit brief modal ──────────────────────────────────────────────────────────

function EditBriefModal({ brief, clients, onSave, onClose }) {
  const [title, setTitle]         = useState(brief.title);
  const [description, setDesc]    = useState(brief.description || '');
  const [clientId, setClientId]   = useState(brief.client_id ? String(brief.client_id) : '');
  const [shootDate, setShootDate] = useState(brief.shoot_date || '');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const updated = await api.briefs.update(brief.id, {
        title, description,
        client_id: clientId ? Number(clientId) : null,
        shoot_date: shootDate,
      });
      onSave(updated);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  const iS = { width: '100%', background: '#fff', border: `1px solid ${BORDER}`, color: TEXT, fontSize: 13, padding: '11px 13px', outline: 'none', fontFamily: FONT, boxSizing: 'border-box', marginBottom: 16 };
  const lS = { fontSize: SZ, letterSpacing: '0.06em', color: MUTED, textTransform: 'uppercase', display: 'block', marginBottom: 8, fontFamily: FONT };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(242,242,238,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(3px)', padding: '0 16px' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', border: `1px solid ${BORDER}`, padding: 32, width: '100%', maxWidth: 440, boxShadow: '0 4px 28px rgba(0,0,0,0.08)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ fontSize: SZ, letterSpacing: '0.06em', color: MUTED, textTransform: 'uppercase', marginBottom: 28, fontFamily: FONT }}>
          Edit Brief
        </div>
        <form onSubmit={submit}>
          <label style={lS}>Title</label>
          <input style={iS} value={title} onChange={e => setTitle(e.target.value)} required autoFocus />
          <label style={lS}>Description</label>
          <input style={iS} value={description} onChange={e => setDesc(e.target.value)} />
          <label style={lS}>Shoot Date</label>
          <input style={iS} value={shootDate} onChange={e => setShootDate(e.target.value)} placeholder="e.g. June 2026" />
          <label style={lS}>Client</label>
          <select style={{ ...iS, marginBottom: 24 }} value={clientId} onChange={e => setClientId(e.target.value)}>
            <option value="">— Unassigned —</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.display_name || c.username}</option>)}
          </select>
          {error && <div style={{ fontSize: SZ, color: '#c0392b', marginBottom: 16 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="submit" disabled={saving} style={{ background: TEXT, color: '#fff', border: 'none', padding: '11px 24px', fontSize: SZ, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: saving ? 'default' : 'pointer', fontFamily: FONT }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button type="button" onClick={onClose} style={{ background: 'transparent', color: MUTED, border: `1px solid ${BORDER}`, padding: '10px 20px', fontSize: SZ, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: FONT }}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── R2 Library panel ──────────────────────────────────────────────────────────

function LibraryPanel({ onAssign, onClose }) {
  const [images, setImages]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dropping, setDropping]   = useState(false);

  useEffect(() => {
    api.media.list()
      .then(setImages)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  async function uploadFile(file) {
    if (!file?.type.startsWith('image/')) return;
    setUploading(true);
    try {
      const result = await api.media.upload(file);
      setImages(prev => [result, ...prev]);
    } catch (err) { alert(err.message); }
    finally { setUploading(false); setDropping(false); }
  }

  return (
    <div
      style={{ padding: 16, overflowY: 'auto', height: '100%', boxSizing: 'border-box', userSelect: 'none' }}
      onDragOver={e => { e.preventDefault(); setDropping(true); }}
      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDropping(false); }}
      onDrop={e => { e.preventDefault(); setDropping(false); uploadFile(e.dataTransfer.files[0]); }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <span style={{ fontSize: SZ, letterSpacing: '0.06em', color: MUTED, textTransform: 'uppercase', fontFamily: FONT }}>Library</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: SZ, color: MUTED, fontFamily: FONT, padding: 0, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Close</button>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px dashed ${dropping ? TEXT : BORDER}`, padding: '10px 8px', marginBottom: 14, cursor: 'pointer', transition: 'border-color 0.15s' }}>
        <input type="file" accept="image/*" multiple style={{ display: 'none' }}
          onChange={e => Array.from(e.target.files).forEach(uploadFile)} />
        <span style={{ fontSize: SZ, letterSpacing: '0.06em', color: dropping ? TEXT : MUTED, textTransform: 'uppercase', fontFamily: FONT }}>
          {uploading ? 'Uploading…' : dropping ? 'Drop to upload' : '+ Upload'}
        </span>
      </label>

      {loading ? (
        <div style={{ fontSize: SZ, color: MUTED, fontFamily: FONT, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Loading…</div>
      ) : images.length === 0 ? (
        <div style={{ fontSize: SZ, color: MUTED, fontFamily: FONT, letterSpacing: '0.06em', textTransform: 'uppercase' }}>No images yet</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {images.map(img => (
            <div
              key={img.key}
              draggable
              onDragStart={e => {
                e.dataTransfer.setData('application/r2-image', img.src);
                e.dataTransfer.effectAllowed = 'copy';
              }}
              style={{ position: 'relative', border: `1px solid ${BORDER}`, cursor: 'grab' }}
            >
              <img
                src={`${api.BASE_URL}${img.src}`} alt=""
                style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
              />
              <button
                onClick={() => onAssign(img.src)}
                title="Add as new image card"
                style={{ position: 'absolute', bottom: 4, right: 4, background: 'rgba(242,242,238,0.92)', border: `1px solid ${BORDER}`, width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 13, fontWeight: 'bold', color: TEXT, padding: 0, lineHeight: 1 }}
              >+</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Inline client selector (bottom bar, admin only) ───────────────────────────

function ClientSelector({ brief, clients, onUpdate }) {
  const [saving, setSaving] = useState(false);

  async function handleChange(e) {
    const clientId = e.target.value ? Number(e.target.value) : null;
    setSaving(true);
    try {
      const updated = await api.briefs.update(brief.id, {
        title: brief.title,
        description: brief.description,
        client_id: clientId,
        shoot_date: brief.shoot_date,
      });
      onUpdate(prev => ({ ...prev, ...updated }));
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  }

  return (
    <select
      value={brief.client_id ? String(brief.client_id) : ''}
      onChange={handleChange}
      disabled={saving}
      style={{ background: 'transparent', border: 'none', fontSize: SZ, letterSpacing: '0.06em', color: MUTED, textTransform: 'uppercase', cursor: 'pointer', fontFamily: FONT, outline: 'none', maxWidth: 220 }}
    >
      <option value="">— Unassigned —</option>
      {clients.map(c => <option key={c.id} value={c.id}>{c.display_name || c.username}</option>)}
    </select>
  );
}

// ── Mobile bottom sheet ───────────────────────────────────────────────────────

function BottomSheet({ onDismiss, children }) {
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 100 }}
      onClick={e => e.target === e.currentTarget && onDismiss()}
    >
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        background: BG, borderTop: `1px solid ${BORDER}`,
        borderRadius: '12px 12px 0 0',
        maxHeight: '75vh', overflowY: 'auto',
      }}>
        <div style={{ width: 36, height: 3, background: BORDER, borderRadius: 2, margin: '12px auto 0' }} />
        {children}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function BriefPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isMobile = useIsMobile();

  const [brief, setBrief]                 = useState(null);
  const [clients, setClients]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState('');
  const [editingCard, setEditingCard]     = useState(null);
  const [showAddPanel, setShowAddPanel]   = useState(false);
  const [showEditBrief, setShowEditBrief] = useState(false);
  const [showLibrary, setShowLibrary]     = useState(false);
  const [activeIndex, setActiveIndex]     = useState(0);
  const [hoveredCardId, setHoveredCardId] = useState(null);
  const [viewMode, setViewMode]           = useState(false);
  const [dragSrcIdx, setDragSrcIdx]       = useState(null);
  const [dragOverIdx, setDragOverIdx]     = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.briefs.get(id)
      .then(setBrief)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
    if (isAdmin) api.users.list().then(setClients).catch(() => {});
  }, [id, isAdmin]);

  const cardMargin = isMobile ? 32 : 720;

  // DOM-based scroll: works with any margin setup
  function scrollToCard(index, instant = false) {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.children[index + 1]; // children[0] is left spacer
    if (!card) return;
    const target = Math.max(0, card.offsetLeft - (el.clientWidth - card.offsetWidth) / 2);
    if (instant) {
      el.scrollLeft = target;
    } else {
      el.scrollTo({ left: target, behavior: 'smooth' });
    }
  }

  // Scroll to center the gap between card 0 and card 1 on load (both desktop + mobile)
  function scrollToInitial() {
    const el = scrollRef.current;
    if (!el || !brief?.cards.length) return;
    if (brief.cards.length < 2) { scrollToCard(0, true); return; }
    const card0 = el.children[1];
    const card1 = el.children[2];
    if (!card0 || !card1) return;
    const gapCenter = card0.offsetLeft + card0.offsetWidth + cardMargin / 2;
    el.scrollLeft = Math.max(0, gapCenter - el.clientWidth / 2);
  }

  // On load: center between first two cards; track active card
  useEffect(() => {
    if (!brief) return;
    setActiveIndex(0);
    requestAnimationFrame(() => requestAnimationFrame(() => scrollToInitial()));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brief?.id]);

  useEffect(() => {
    function onKey(e) {
      if (!brief) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowRight') { const n = Math.min(activeIndex + 1, brief.cards.length - 1); setActiveIndex(n); scrollToCard(n); }
      if (e.key === 'ArrowLeft')  { const n = Math.max(activeIndex - 1, 0); setActiveIndex(n); scrollToCard(n); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [brief, activeIndex]);

  async function addCard(type) {
    try {
      await api.briefs.addCard(id, type, defaultContent(type));
      const updated = await api.briefs.get(id);
      setBrief(updated);
      setShowAddPanel(false);
      setTimeout(() => scrollToCard(updated.cards.length - 1), 80);
    } catch (err) { alert(err.message); }
  }

  async function deleteCard(cardId) {
    if (!window.confirm('Delete this card?')) return;
    await api.briefs.deleteCard(id, cardId);
    setBrief(await api.briefs.get(id));
  }

  async function moveCard(cardId, dir) {
    const cards = [...brief.cards];
    const idx = cards.findIndex(c => c.id === cardId);
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= cards.length) return;
    const order = cards.map(c => c.id);
    order.splice(idx, 1);
    order.splice(newIdx, 0, cardId);
    setBrief(await api.briefs.reorderCards(id, order));
  }

  // ── Drag-to-reorder handlers ────────────────────────────────────────────────

  function handleDragStart(e, idx) {
    setDragSrcIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/card-index', String(idx));
  }

  function handleDragOver(e, idx) {
    e.preventDefault();
    const isLibrary = e.dataTransfer.types.includes('application/r2-image');
    e.dataTransfer.dropEffect = isLibrary ? 'copy' : 'move';
    if (!isLibrary && idx !== dragSrcIdx) setDragOverIdx(idx);
    if (isLibrary) setDragOverIdx(idx);
  }

  async function handleDrop(e, cardIdx, card) {
    e.preventDefault();
    setDragOverIdx(null);

    const r2src = e.dataTransfer.getData('application/r2-image');
    if (r2src) {
      if (card.type === 'image' || card.type === 'sketch') {
        try {
          await api.briefs.updateCard(id, card.id, { ...card.content, src: r2src });
          setBrief(await api.briefs.get(id));
        } catch (err) { alert(err.message); }
      }
      setDragSrcIdx(null);
      return;
    }

    if (dragSrcIdx === null || dragSrcIdx === cardIdx) { setDragSrcIdx(null); return; }
    const order = brief.cards.map(c => c.id);
    const [moved] = order.splice(dragSrcIdx, 1);
    order.splice(cardIdx, 0, moved);
    setDragSrcIdx(null);
    try { setBrief(await api.briefs.reorderCards(id, order)); }
    catch (err) { alert(err.message); }
  }

  function handleDragEnd() { setDragSrcIdx(null); setDragOverIdx(null); }

  // ── Library helpers ─────────────────────────────────────────────────────────

  async function assignFromLibrary(src) {
    try {
      await api.briefs.addCard(id, 'image', { src, caption: '' });
      const updated = await api.briefs.get(id);
      setBrief(updated);
      setShowLibrary(false);
      setTimeout(() => scrollToCard(updated.cards.length - 1), 80);
    } catch (err) { alert(err.message); }
  }

  function closePanel() {
    setEditingCard(null);
    setShowAddPanel(false);
    setShowLibrary(false);
  }

  if (loading) return (
    <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT }}>
      <span style={{ fontSize: SZ, letterSpacing: '0.06em', color: MUTED, textTransform: 'uppercase' }}>Loading...</span>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT }}>
      <span style={{ fontSize: SZ, color: '#c0392b' }}>{error}</span>
    </div>
  );

  const backPath = isAdmin ? '/admin' : '/portal';
  const panelOpen = Boolean(editingCard || showAddPanel || showLibrary);

  const panelContent = editingCard ? (
    <CardEditor
      key={editingCard.id}
      card={editingCard}
      briefId={id}
      onUpdate={updated => { setBrief(updated); setEditingCard(null); }}
      onClose={closePanel}
    />
  ) : showAddPanel ? (
    <AddCardPanel onAdd={addCard} onClose={closePanel} />
  ) : showLibrary ? (
    <LibraryPanel onAssign={assignFromLibrary} onClose={closePanel} />
  ) : null;

  return (
    <div style={{ height: '100dvh', background: BG, display: 'flex', flexDirection: 'column', fontFamily: FONT, overflow: 'hidden' }}>

      {/* Top bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: isMobile ? '0 24px' : '0 10px', height: 40,
        borderBottom: `1px solid ${BORDER}`, background: BG, flexShrink: 0,
      }}>
        <a href="/" style={{ fontSize: SZ, letterSpacing: '0.06em', color: TEXT, textTransform: 'uppercase', textDecoration: 'none', flexShrink: 0 }}>
          Pierce Bancroft Townsend
        </a>
        <div style={{ display: 'flex', gap: isMobile ? 12 : 20, alignItems: 'center', flexShrink: 0 }}>
          {isAdmin && !viewMode && (
            <>
              <button onClick={() => { setShowAddPanel(true); setEditingCard(null); setShowLibrary(false); }} style={{ background: 'transparent', border: 'none', color: TEXT, fontSize: SZ, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: FONT, padding: 0 }}>
                + Card
              </button>
              <button onClick={() => { setShowLibrary(v => !v); setEditingCard(null); setShowAddPanel(false); }} style={{ background: 'transparent', border: 'none', color: TEXT, fontSize: SZ, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: FONT, padding: 0 }}>
                Library
              </button>
              <button onClick={() => setShowEditBrief(true)} style={{ background: 'transparent', border: 'none', color: TEXT, fontSize: SZ, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: FONT, padding: 0 }}>
                Edit
              </button>
            </>
          )}
          {isAdmin && (
            <button
              onClick={() => { setViewMode(v => !v); if (!viewMode) closePanel(); }}
              style={{ background: 'transparent', border: 'none', color: TEXT, fontSize: SZ, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: FONT, padding: 0 }}
            >
              {viewMode ? 'Edit' : 'View'}
            </button>
          )}
          <span style={{ fontSize: SZ, letterSpacing: '0.06em', color: MUTED }}>
            {brief.cards.length > 0 ? `${activeIndex + 1}/${brief.cards.length}` : '—'}
          </span>
        </div>
      </div>

      {/* Cards + desktop side panel */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {brief.cards.length === 0 ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20 }}>
              <div style={{ fontSize: SZ, letterSpacing: '0.06em', color: MUTED, textTransform: 'uppercase' }}>No cards yet</div>
              {isAdmin && (
                <button onClick={() => setShowAddPanel(true)} style={{ background: 'transparent', border: `1px solid ${BORDER}`, color: MUTED, padding: '10px 24px', fontSize: SZ, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: FONT }}>
                  Add First Card
                </button>
              )}
            </div>
          ) : (
            <div
              ref={scrollRef}
              className="hide-scrollbar"
              style={{ flex: 1, display: 'flex', overflowX: 'auto', overflowY: 'hidden', scrollSnapType: 'x mandatory' }}
              onScroll={e => {
                const el = e.currentTarget;
                const viewCenter = el.scrollLeft + el.clientWidth / 2;
                let closest = 0, closestDist = Infinity;
                brief.cards.forEach((_, i) => {
                  const card = el.children[i + 1];
                  if (!card) return;
                  const dist = Math.abs((card.offsetLeft + card.offsetWidth / 2) - viewCenter);
                  if (dist < closestDist) { closestDist = dist; closest = i; }
                });
                setActiveIndex(closest);
              }}
            >
              {/* Left + right spacers let first and last cards snap to center */}
              <div style={{ width: `calc((100vw - min(1125px, 94vw)) / 2)`, minWidth: `calc((100vw - min(1125px, 94vw)) / 2)`, flexShrink: 0 }} />

              {brief.cards.map((card, cardIdx) => (
                <div
                  key={card.id}
                  onMouseEnter={() => setHoveredCardId(card.id)}
                  onMouseLeave={() => setHoveredCardId(null)}
                  onDragOver={isAdmin && !viewMode ? e => handleDragOver(e, cardIdx) : undefined}
                  onDrop={isAdmin && !viewMode ? e => handleDrop(e, cardIdx, card) : undefined}
                  onDragLeave={isAdmin && !viewMode ? () => setDragOverIdx(null) : undefined}
                  onClick={cardIdx !== activeIndex ? () => { setActiveIndex(cardIdx); scrollToCard(cardIdx); } : undefined}
                  style={{
                    width: CARD_W, minWidth: CARD_W, height: '100%', flexShrink: 0,
                    scrollSnapAlign: 'center', background: BG, position: 'relative',
                    marginRight: cardMargin,
                    opacity: dragSrcIdx === cardIdx ? 0.35 : 1,
                    outline: dragOverIdx === cardIdx && dragSrcIdx !== cardIdx ? `2px solid ${TEXT}` : 'none',
                    transition: 'opacity 0.15s',
                    cursor: cardIdx !== activeIndex ? 'pointer' : 'default',
                  }}
                >
                  <CardContent card={card} isAdmin={isAdmin && !viewMode} briefId={id} onRefresh={() => api.briefs.get(id).then(setBrief)} />

                  {isAdmin && !viewMode && (
                    <div style={{
                      position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
                      display: 'flex', gap: 6,
                      opacity: (isMobile || hoveredCardId === card.id) ? 1 : 0,
                      transition: 'opacity 0.2s',
                      pointerEvents: (isMobile || hoveredCardId === card.id) ? 'auto' : 'none',
                    }}>
                      {/* Drag handle */}
                      <button
                        draggable
                        onDragStart={e => handleDragStart(e, cardIdx)}
                        onDragEnd={handleDragEnd}
                        style={{
                          background: 'rgba(242,242,238,0.96)', border: `1px solid ${BORDER}`,
                          color: MUTED, padding: isMobile ? '9px 13px' : '7px 11px',
                          fontSize: SZ, letterSpacing: '0.06em',
                          cursor: 'grab', fontFamily: FONT, textTransform: 'uppercase',
                          boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
                        }}
                        title="Drag to reorder"
                      >≡</button>
                      {[
                        { label: '←', action: () => moveCard(card.id, -1) },
                        { label: 'Edit', action: () => { setEditingCard(card); setShowAddPanel(false); setShowLibrary(false); } },
                        { label: '×', action: () => deleteCard(card.id) },
                        { label: '→', action: () => moveCard(card.id, 1) },
                      ].map(btn => (
                        <button key={btn.label} onClick={btn.action} style={{
                          background: 'rgba(242,242,238,0.96)', border: `1px solid ${BORDER}`,
                          color: TEXT, padding: isMobile ? '9px 15px' : '7px 13px',
                          fontSize: SZ, letterSpacing: '0.06em',
                          cursor: 'pointer', fontFamily: FONT, textTransform: 'uppercase',
                          boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
                        }}>{btn.label}</button>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Right spacer so last card can snap to center */}
              <div style={{ width: `calc((100vw - min(1125px, 94vw)) / 2)`, minWidth: `calc((100vw - min(1125px, 94vw)) / 2)`, flexShrink: 0 }} />

            </div>
          )}


        </div>

        {/* Desktop side panel */}
        {isAdmin && !viewMode && panelOpen && !isMobile && (
          <div style={{ width: showLibrary ? 320 : 280, borderLeft: `1px solid ${BORDER}`, background: BG, display: 'flex', flexDirection: 'column', overflowY: 'auto', flexShrink: 0 }}>
            {panelContent}
          </div>
        )}
      </div>

      {/* Bottom nav — 3-column grid: Back | dots | client info */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center',
        padding: isMobile ? '0 24px' : '0 10px', height: 40,
        borderTop: `1px solid ${BORDER}`, background: BG, flexShrink: 0,
      }}>
        <Link to={backPath} style={{ fontSize: SZ, letterSpacing: '0.06em', color: TEXT, textTransform: 'uppercase', textDecoration: 'none' }}>
          ← Back
        </Link>

        {/* Progress dots — centered column */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {brief.cards.map((_, i) => (
            <button key={i} onClick={() => { setActiveIndex(i); scrollToCard(i); }} style={{
              width: i === activeIndex ? 28 : 5, height: i === activeIndex ? 3 : 2,
              background: i === activeIndex ? TEXT : BORDER,
              border: 'none', padding: 0, cursor: 'pointer', transition: 'all 0.25s', flexShrink: 0,
            }} />
          ))}
        </div>

        {/* Client / date — right-aligned */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {isAdmin && !viewMode ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ClientSelector brief={brief} clients={clients} onUpdate={setBrief} />
              {brief.shoot_date && (
                <span style={{ fontSize: SZ, letterSpacing: '0.06em', color: MUTED, textTransform: 'uppercase' }}>· {brief.shoot_date}</span>
              )}
            </div>
          ) : (
            (brief.client_name || brief.shoot_date) && (
              <div style={{ fontSize: SZ, letterSpacing: '0.06em', color: MUTED, textTransform: 'uppercase' }}>
                {[brief.client_name, brief.shoot_date].filter(Boolean).join(' · ')}
              </div>
            )
          )}
        </div>
      </div>

      {/* Mobile FAB — quick add card, only when panel is closed */}
      {isAdmin && !viewMode && isMobile && !panelOpen && brief.cards.length > 0 && (
        <button
          onClick={() => setShowAddPanel(true)}
          style={{
            position: 'fixed', bottom: 56, right: 16, zIndex: 20,
            width: 36, height: 36, background: TEXT, color: '#fff',
            border: 'none', cursor: 'pointer', fontFamily: FONT,
            fontSize: 20, lineHeight: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 14px rgba(0,0,0,0.18)',
          }}
        >+</button>
      )}

      {/* Mobile bottom sheet */}
      {isAdmin && !viewMode && panelOpen && isMobile && (
        <BottomSheet onDismiss={closePanel}>
          {panelContent}
        </BottomSheet>
      )}

      {/* Edit brief modal */}
      {showEditBrief && (
        <EditBriefModal
          brief={brief}
          clients={clients}
          onSave={updated => { setBrief(prev => ({ ...prev, ...updated })); setShowEditBrief(false); }}
          onClose={() => setShowEditBrief(false)}
        />
      )}
    </div>
  );
}
